import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Iconify from "../iconify";
import { get } from "src/utils/networkUtils";

const LIMIT = 25;

export function formatUserDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
}

export function buildAppUsersUrl({
  offset = 0,
  limit = LIMIT,
  search = "",
  filters = {},
}) {
  const params = new URLSearchParams({
    offset: String(offset),
    limit: String(limit),
  });

  if (search) params.set("search", search);
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, value);
    }
  });

  return `/admin/app-users?${params.toString()}`;
}

export async function fetchAppUsersPage({
  offset = 0,
  limit = LIMIT,
  search = "",
  filters = {},
}) {
  const response = await get(
    buildAppUsersUrl({ offset, limit, search, filters })
  );
  return response?.data ?? { users: [], total: 0 };
}

export default function AppUserSearchTable({
  actionLabel,
  emptyLabel,
  filters,
  onSelectUser,
  searchLabel,
  toolbar,
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const filterKey = useMemo(() => JSON.stringify(filters ?? {}), [filters]);

  useEffect(() => {
    setOffset(0);
    setRows([]);
    setHasMore(true);
  }, [searchQuery, filterKey]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setLoading(true);
      fetchAppUsersPage({
        offset,
        limit: LIMIT,
        search: searchQuery.trim(),
        filters,
      })
        .then((payload) => {
          const users = payload.users ?? [];
          setTotal(payload.total ?? 0);
          setRows((prev) => (offset === 0 ? users : [...prev, ...users]));
          setHasMore(users.length === LIMIT);
        })
        .finally(() => setLoading(false));
    }, 350);

    return () => clearTimeout(handle);
  }, [filters, filterKey, offset, searchQuery]);

  const lastRowRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setOffset((prev) => prev + LIMIT);
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasMore, loading]
  );

  return (
    <Card>
      <Box sx={{ p: 2.5 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <TextField
            label={searchLabel}
            type="text"
            variant="standard"
            onInput={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-outline" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 360 }}
          />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
          >
            {toolbar}
            <Typography variant="body2" color="text.secondary">
              {total} registered user{total === 1 ? "" : "s"}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>EngageCraft ID</TableCell>
              <TableCell>Ref ID</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Fav Club</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Login</TableCell>
              {onSelectUser && <TableCell align="right">Action</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={row.id}
                ref={index === rows.length - 1 ? lastRowRef : undefined}
                hover
                onClick={() => onSelectUser?.(row)}
                sx={{ cursor: onSelectUser ? "pointer" : "default" }}
              >
                <TableCell>{row.username ?? "-"}</TableCell>
                <TableCell>{row.engagecraftId}</TableCell>
                <TableCell>{row.refId}</TableCell>
                <TableCell>
                  {(row.refCountryCode ?? "").trim() || "-"}
                </TableCell>
                <TableCell>{row.refFavClubId ?? "-"}</TableCell>
                <TableCell>{formatUserDate(row.createdAt)}</TableCell>
                <TableCell>{formatUserDate(row.lastLoginAt)}</TableCell>
                {onSelectUser && (
                  <TableCell align="right">
                    <Button size="small" variant="outlined">
                      {actionLabel}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={onSelectUser ? 8 : 7}
                  align="center"
                  sx={{ py: 4 }}
                >
                  {emptyLabel}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Card>
  );
}

AppUserSearchTable.propTypes = {
  actionLabel: PropTypes.string,
  emptyLabel: PropTypes.string,
  filters: PropTypes.object,
  onSelectUser: PropTypes.func,
  searchLabel: PropTypes.string,
  toolbar: PropTypes.node,
};

AppUserSearchTable.defaultProps = {
  actionLabel: "Open",
  emptyLabel: "No users found.",
  filters: {},
  searchLabel: "Search by username or ref ID",
  toolbar: null,
};
